import { useEffect, useRef, useState } from "react";
import { LiaHornbill, LiaSearchSolid } from "react-icons/lia";
import { MdClose, MdProductionQuantityLimits } from "react-icons/md";
import { dieMasterAPI } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import Button from "../../common/Button";
import { BsSendFill } from "react-icons/bs";
import { DiEnvato } from "react-icons/di";
import { formatDateTime } from "../../utlis/formateDate";
import { STATUS_COLORS } from "../../utlis/constant";

const DieAllocation = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const [searchQuery, setSearchQuery] = useState("");
  const [isDiePreparedData, setIsDiePreparedData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeTab, setActiveTab] = useState("die-preparation");
  const [preparedPage, setPreparedPage] = useState(1);
  const [preparedHasMore, setPreparedHasMore] = useState(true);
  const [sendOvenData, setSendOvenData] = useState([]);
  const [sendOvenPage, setSendOvenPage] = useState(1);
  const [sendOvenHasMore, setSendOvenHasMore] = useState(true);
  const pageSize = 50;
  const tableScrollRef = useRef(null);
  const isFetchingRef = useRef(false);
  const [loading, setLoading] = useState(false);

  const fetchDiePreparationData = async (page) => {
    try {
      setLoading(true);
      const res = await dieMasterAPI.getAllDieAllocation(page, pageSize);

      setIsDiePreparedData((prev) => {
        return page === 1 ? res.data || [] : [...prev, ...(res.data || [])];
      });

      if (!res.data || res.data.length < pageSize) {
        setPreparedHasMore(false);
      }
      isFetchingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const fetchSendtoovenData = async (page) => {
    try {
      setLoading(true);
      const res = await dieMasterAPI.getAllOvenIssuedData(page, pageSize);

      setSendOvenData((prev) => {
        return page === 1 ? res.data || [] : [...prev, ...(res.data || [])];
      });

      if (!res.data || res.data.length < pageSize) {
        setSendOvenHasMore(false);
      }
      isFetchingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "die-preparation") {
      setPreparedPage(1);
      setIsDiePreparedData([]);
      setPreparedHasMore(true);
    } else {
      setSendOvenPage(1);
      setSendOvenData([]);
      setSendOvenHasMore(true);
    }
  };

  useEffect(() => {
    if (activeTab === "die-preparation") fetchDiePreparationData(preparedPage);
  }, [preparedPage, activeTab]);

  useEffect(() => {
    if (activeTab === "send-to-oven") fetchSendtoovenData(sendOvenPage);
  }, [sendOvenPage, activeTab]);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;

        if (activeTab === "die-preparation" && preparedHasMore) {
          setPreparedPage((p) => p + 1);
        }

        if (activeTab === "send-to-oven" && sendOvenHasMore) {
          setSendOvenPage((p) => p + 1);
        }
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [activeTab, loading, preparedHasMore, sendOvenHasMore]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setIsDiePreparedData(isDiePreparedData);
    } else {
      const filtered = isDiePreparedData.filter((d) =>
        d.status_Name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setIsDiePreparedData(filtered);
    }
  }, [searchQuery, isDiePreparedData]);

  const handleSendToOven = async () => {
    if (selectedIds.length === 0) {
      notify("Please select at least one die", "error");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        dieIds: selectedIds,
        createdBy: Number(userId),
      };

      await dieMasterAPI.sendToOven(payload);

      notify(`Successfully sent ${selectedIds.length} dies to Oven`, "success");

      setSelectedIds([]);
      setPreparedPage(1);
      setIsDiePreparedData([]);
      fetchDiePreparationData(1);

      setSendOvenPage(1);
      setSendOvenData([]);
      fetchSendtoovenData(1);
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to send to oven";
      notify(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const ovenColumns = [
    { key: "dieNumber", label: "Die no" },
    { key: "currentWeight", label: "Current Weight" },
    {
      key: "status_Name",
      label: "Die Status",
      render: (val) => {
        const color = STATUS_COLORS[val] || "bg-slate-100 text-slate-600";
        return (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}
          >
            {val}
          </span>
        );
      },
    },
    { key: "location_Name", label: "Location" },
    {
      key: "oven_Start_Datetime",
      label: "Oven Time",
      render: (value) => formatDateTime(value),
    },
  ];

  const columns = [
    {
      key: "select",
      label: (
        <input
          type="checkbox"
          className="cursor-pointer h-4 w-4"
          checked={
            isDiePreparedData.length > 0 &&
            selectedIds.length === isDiePreparedData.length
          }
          onChange={(e) => {
            if (e.target.checked) {
              const allIds = isDiePreparedData.map((i) => i.dieId);
              setSelectedIds(allIds);
            } else {
              setSelectedIds([]);
            }
          }}
        />
      ),
      render: (_, row) => (
        <input
          type="checkbox"
          className="cursor-pointer h-4 w-4"
          checked={selectedIds.includes(row.dieId)}
          onChange={() => {
            const rowId = row.dieId;
            setSelectedIds((prev) =>
              prev.includes(rowId)
                ? prev.filter((id) => id !== rowId)
                : [...prev, rowId],
            );
          }}
        />
      ),
    },
    { key: "dieNumber", label: "Die no" },
    { key: "currentWeight", label: "Current Weight" },
    {
      key: "status_Name",
      label: "Status",
      render: (val) => {
        const color = STATUS_COLORS[val] || "bg-slate-100 text-slate-600";
        return (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}
          >
            {val}
          </span>
        );
      },
    },
    { key: "location_Name", label: "Location" },
    { key: "oven_Start_Datetime", label: "Oven Time" },
  ];

  const renderDetailTable = (row) => {
    if (!row.details || row.details.length === 0) {
      return (
        <div className="text-[11px] text-slate-400 p-3 italic bg-slate-50/50">
          No batch details available.
        </div>
      );
    }

    const totalPcs = row.details.reduce(
      (sum, item) => sum + (Number(item.qty) || 0),
      0,
    );
    const totalWeight = row.details.reduce(
      (sum, item) => sum + (Number(item.qty_Weight) || 0),
      0,
    );

    return (
      <div className="bg-slate-50 max-w-xl border-y border-slate-100">
        <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
          <table className="w-full  text-[12px] border-collapse bg-white">
            <thead className="bg-slate-800 text-white uppercase tracking-wider font-semibold">
              <tr>
                <th className="px-4 py-2 text-left">SO NO</th>
                <th className="px-4 py-2 text-center">Cut Length</th>
                <th className="px-4 py-2 text-right">Pcs</th>
                <th className="px-4 py-2 text-right">Weight (kg)</th>
                <th className="px-4 py-2 text-right">Order Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {row.details.map((item, i) => (
                <tr key={i} className="hover:bg-blue-50/40 transition-colors">
                  <td className="px-4 py-1.5 font-bold text-blue-600 underline cursor-pointer">
                    {item.order_No}
                  </td>
                  <td className="px-4 py-1.5 text-right text-slate-600">
                    {Number(item.profile_Length).toFixed(2)}
                  </td>
                  <td className="px-4 py-1.5 text-right font-semibold text-slate-800">
                    {item.qty}
                  </td>
                  <td className="px-4 py-1.5 text-right text-emerald-600 font-bold">
                    {Number(item.qty_Weight).toFixed(3)}
                  </td>
                  <td className="px-4 py-1.5 text-right text-blue-600 font-bold">
                    {item.order_Status}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-100/80 font-black border-t-2 border-slate-200">
              <tr>
                <td className="px-4 py-2 text-slate-500 uppercase">
                  Grand Total
                </td>
                <td className="px-4 py-2"></td>
                <td className="px-4 py-2 text-right text-slate-900 text-sm">
                  {totalPcs}
                </td>
                <td className="px-4 py-2 text-right text-emerald-700 text-sm">
                  {totalWeight.toFixed(3)}
                </td>
                <td className="px-4 py-2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  const renderDieDetailTable = (row) => {
    if (!row.details || row.details.length === 0) {
      return (
        <div className="text-[11px] text-slate-400 p-3 italic bg-slate-50/50">
          No batch details available.
        </div>
      );
    }

    const totalPcs = row.details.reduce(
      (sum, item) => sum + (Number(item.qty) || 0),
      0,
    );
    const totalWeight = row.details.reduce(
      (sum, item) => sum + (Number(item.qty_Weight) || 0),
      0,
    );

    return (
      <div className="bg-slate-50 max-w-xl border-y border-slate-100">
        <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
          <table className="w-full  text-[12px] border-collapse bg-white">
            <thead className="bg-slate-800 text-white uppercase tracking-wider font-semibold">
              <tr>
                <th className="px-4 py-2 text-left">SO NO</th>
                <th className="px-4 py-2 text-center">Cut Length (mm)</th>
                <th className="px-4 py-2 text-right">Pcs</th>
                <th className="px-4 py-2 text-right">Weight (kg)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {row.details.map((item, i) => (
                <tr key={i} className="hover:bg-blue-50/40 transition-colors">
                  <td className="px-4 py-1.5 font-bold text-blue-600 underline cursor-pointer">
                    {item.order_No}
                  </td>
                  <td className="px-4 py-1.5 text-right text-slate-600">
                    {Number(item.profile_Length).toFixed(2)}
                  </td>
                  <td className="px-4 py-1.5 text-right font-semibold text-slate-800">
                    {item.qty}
                  </td>
                  <td className="px-4 py-1.5 text-right text-emerald-600 font-bold">
                    {Number(item.qty_Weight).toFixed(3)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-100/80 font-black border-t-2 border-slate-200">
              <tr>
                <td className="px-4 py-2 text-slate-500 uppercase">
                  Grand Total
                </td>
                <td className="px-4 py-2"></td>
                <td className="px-4 py-2 text-right text-slate-900 text-sm">
                  {totalPcs}
                </td>
                <td className="px-4 py-2 text-right text-emerald-700 text-sm">
                  {totalWeight.toFixed(3)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  const totalPcs = isDiePreparedData
    .filter((item) => selectedIds.includes(item.dieId))
    .reduce((sum, item) => {
      const pcs = (item.details ?? []).reduce(
        (s, d) => s + (Number(d.qty) || 0),
        0,
      );
      return sum + pcs;
    }, 0);

  const totalWeight = isDiePreparedData
    .filter((item) => selectedIds.includes(item.dieId))
    .reduce((sum, item) => {
      const w = (item.details ?? []).reduce(
        (s, d) => s + (Number(d.qty_Weight) || 0),
        0,
      );
      return sum + w;
    }, 0)
    .toFixed(3);

  return (
    <>
      <div>
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-1">
            <div className="space-y-1">
              <h2 className="text-2xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
                Die Send to On
              </h2>
              <p className="text-sm text-slate-500 font-medium">
                Manage and track customer die-preparation orders efficiently
              </p>
            </div>

            <div className="inline-flex p-1 bg-slate-200/50 rounded-xl backdrop-blur-sm">
              <button
                onClick={() => handleTabChange("die-preparation")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === "die-preparation"
                    ? "bg-white text-primary shadow-sm"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                <DiEnvato size={18} /> Die Preparation
              </button>
              <button
                onClick={() => handleTabChange("send-to-oven")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === "send-to-oven"
                    ? "bg-white text-primary shadow-sm"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                <BsSendFill size={18} /> Send to Oven
              </button>
              <button
                onClick={() => handleTabChange("production-done")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === "production-done"
                    ? "bg-white text-primary shadow-sm"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                <MdProductionQuantityLimits size={18} /> Production Done
              </button>
            </div>
          </div>

          {activeTab === "die-preparation" && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="relative w-full sm:w-80 group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <LiaSearchSolid className="h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                </div>
                <input
                  type="text"
                  className="w-full bg-slate-50 border border-slate-200 py-2.5 pl-11 pr-10 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all placeholder:text-slate-400 text-sm"
                  placeholder="Search by order, customer..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <MdClose size={20} />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                <Button
                  text={`Send To Oven (${selectedIds.length})`}
                  icon={LiaHornbill}
                  disabled={selectedIds.length === 0}
                  onClick={handleSendToOven}
                  className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 ${
                    selectedIds.length > 0
                      ? "bg-orange text-white shadow-lg shadow-orange-200 hover:bg-orange-700 hover:-translate-y-0.5 active:scale-95 cursor-pointer opacity-100 visibility-visible"
                      : "bg-slate-100 text-slate-400 cursor-not-allowed opacity-60"
                  }`}
                  iconSpacing="mr-2"
                  iconColor={
                    selectedIds.length > 0 ? "text-white" : "text-slate-400"
                  }
                />
              </div>
            </div>
          )}
        </div>
        <div
          ref={tableScrollRef}
          className="relative overflow-auto border border-slate-200 rounded-xl shadow-inner bg-white"
          style={{ height: "calc(100vh - 320px)" }}
        >
          {loading && preparedPage && sendOvenPage === 1 ? (
            <Loader />
          ) : activeTab === "die-preparation" ? (
            <div className="min-w-max">
              <Table
                columns={columns}
                data={isDiePreparedData}
                showActions={false}
                renderExpansion={renderDieDetailTable}
              />
            </div>
          ) : (
            <div className="min-w-max">
              <Table
                columns={ovenColumns}
                data={sendOvenData}
                showActions={false}
                renderExpansion={renderDetailTable}
              />
            </div>
          )}
        </div>
        {selectedIds.length > 0 && activeTab === "die-preparation" && (
          <div className="flex justify-center mb-4">
            <div className="bg-[#1e293b] text-white px-8 py-3 rounded-2xl shadow-2xl flex items-center gap-12 border border-slate-700 animate-bounce-subtle">
              <div className="flex flex-col items-center">
                <span className="text-[10px] uppercase tracking-[2px] text-slate-400 font-bold">
                  Total Pieces
                </span>
                <span className="text-xl font-black">{totalPcs}</span>
              </div>

              <div className="h-8 w-[1px] bg-slate-600"></div>

              <div className="flex flex-col items-center">
                <span className="text-[10px] uppercase tracking-[2px] text-slate-400 font-bold">
                  Total Weight
                </span>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-black text-orange-400">
                    {totalWeight}
                  </span>
                  <span className="text-[10px] text-slate-400 font-bold">
                    KG
                  </span>
                </div>
              </div>

              <button
                onClick={() => setSelectedIds([])}
                className="bg-slate-700/50 p-2 rounded-full hover:bg-red-500 transition-colors"
              >
                <MdClose size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DieAllocation;
